import React, { useState, useCallback } from 'react';
import { Icon, SelectMenu } from '@scale/scaleui';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import BuilderHint from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderHint';
import { AdditionalConstraint, AdditionalConstraintType } from '@scale/shared-types';
import { AdditionalConstraintsSchemaMap } from '@scale/shared/pipelinev3/genai';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface ResponseConstraintsBuilderProps {
  name: string;
  existingValue?: Array<AdditionalConstraint>;
  updateProperty: (arg: Array<AdditionalConstraint> | undefined) => void;
  required?: boolean;
  hint?: string;
  taskTemplateParams?: Record<string, any>;
  typeBuilderMap: TypeBuilderMapType;
}

const constraintTypeOptions = [
  {
    id: AdditionalConstraintType.FieldId,
    name: 'Field ID',
  },
  {
    id: AdditionalConstraintType.FreeText,
    name: 'Free Text',
  },
];

const ResponseConstraintsBuilder = ({
  name,
  existingValue = [],
  updateProperty,
  required,
  hint,
  taskTemplateParams,
  typeBuilderMap,
}: ResponseConstraintsBuilderProps) => {
  const constraints = existingValue;
  const [selectedType, setSelectedType] = useState<AdditionalConstraintType>(
    AdditionalConstraintType.FieldId,
  );

  const addConstraint = useCallback(
    (type: AdditionalConstraintType) => {
      const newConstraints = [...constraints];
      if (type === AdditionalConstraintType.FieldId) {
        newConstraints.push({ type, fieldId: '' });
      } else if (type === AdditionalConstraintType.FreeText) {
        newConstraints.push({ type, freeText: '' });
      }
      updateProperty(newConstraints);
    },
    [constraints, updateProperty],
  );

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      <label className="block text-sm font-medium leading-6 text-neutral-700">
        {name}
        {required && <span className="text-danger-400">*</span>}
      </label>
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={constraints}
        setElements={updateProperty}
        getKey={constraint => JSON.stringify(constraint)}
        getTitle={(_, idx) => `Constraint ${idx + 1}`}
        getSchema={constraint => AdditionalConstraintsSchemaMap[constraint.type]}
        taskTemplateParams={taskTemplateParams}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={constraintTypeOptions}
          selectedOptionId={selectedType}
          setSelected={value => {
            setSelectedType(value as AdditionalConstraintType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addConstraint(selectedType)}
          size="lg"
          variant="primary"
        />
        {hint && <BuilderHint name={name} hint={hint} />}
      </div>
    </div>
  );
};

export default ResponseConstraintsBuilder;
